import {
  Button,
  Card,
  Flex,
  Group,
  Radio,
  Stack,
  Text,
  Textarea,
  useMantineColorScheme,
} from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Modal, TextSerif } from "@/components/elements";
import { IconTriangle } from "@/components/icons";

import type { DeleteAccountParams, ReasonOption } from "@/services/profile/delete-account-request";
import type { FC } from "react";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (params?: DeleteAccountParams) => void;
  isPending?: boolean;
}

export const DeleteAccountModal: FC<Props> = ({ isOpen, onClose, onConfirm, isPending }) => {
  const { t } = useTranslation();
  const [selectedReason, setSelectedReason] = useState<ReasonOption | undefined>(undefined);
  const [otherText, setOtherText] = useState<string>("");

  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";
  const grayText = isDark ? "dimmed" : "secondary.4";

  const reasons: { value: ReasonOption; label: string }[] = [
    {
      value: "not_satisfied",
      label: t("profile.delete_account.options.not_satisfied"),
    },
    { value: "missing_features", label: t("profile.delete_account.options.missing_features") },
    {
      value: "not_using",
      label: t("profile.delete_account.options.not_using"),
    },
    { value: "other", label: t("profile.delete_account.options.other") },
  ];

  const handleDelete = () => {
    onConfirm({
      reason: selectedReason,
      competitor: otherText || undefined,
    });
  };

  const handleClose = () => {
    if (!isPending) {
      setSelectedReason(undefined);
      setOtherText("");
      onClose();
    }
  };

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      title={t("profile.delete_account.modal_title")}
      centered
      size="lg"
      closeOnClickOutside={!isPending}
      closeOnEscape={!isPending}
    >
      <Stack gap="lg">
        <Text size="md" c="dimmed">
          {t("profile.delete_account.modal_content")}
        </Text>

        <Radio.Group
          value={selectedReason ?? ""}
          onChange={(value) => setSelectedReason(value as ReasonOption)}
        >
          <Stack gap="md">
            {reasons.map((reason) => (
              <Radio
                key={reason.value}
                value={reason.value}
                display="flex"
                styles={{
                  body: { alignItems: "center" },
                  labelWrapper: { alignItems: "center" },
                }}
                aria-label={reason.label}
                label={
                  <TextSerif fw={400} c={isDark ? "dimmed" : "dark"} fz="1.0625em">
                    {reason.label}
                  </TextSerif>
                }
              />
            ))}
          </Stack>
        </Radio.Group>

        <Stack gap="xs" mt="sm">
          <Flex justify="space-between" align="center">
            <Text c={grayText} size="sm">
              {t("profile.delete_account.options.switching_to_competitor")}
            </Text>
          </Flex>
          <Textarea
            placeholder={t("common.enter_a_description") + ".."}
            value={otherText}
            onChange={(e) => setOtherText(e.currentTarget.value)}
            minRows={5}
            disabled={isPending}
          />
        </Stack>

        <Card w="100%" p="md" bg="#f8f4ce" bdrs="md" shadow="sm">
          <Flex align="center" justify="start" gap="md">
            <IconTriangle color="var(--mantine-color-warning-8)" size={50} />
            <TextSerif c="dark">{t("profile.delete_account.delete_info_warning")}</TextSerif>
          </Flex>
        </Card>

        {/* Actions */}
        <Group justify="space-between" mt="sm">
          <Button
            variant="outline"
            onClick={handleClose}
            aria-label={t("common.back")}
            size="md"
            flex={1}
            disabled={isPending}
          >
            {t("common.back")}
          </Button>
          <Button
            color="primary"
            onClick={handleDelete}
            size="md"
            flex={1}
            loading={isPending}
            disabled={isPending}
            aria-label={t("profile.delete_account.next")}
          >
            {t("common.next")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
